import { Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { LocaleLayout } from "./LocaleLayout";
import { Home } from "../pages/Home";
import { Collections } from "../pages/Collections";
// Dettaglio collezione DISATTIVATO: le pagine /collezioni/:slug (e /collections/:slug)
// non sono più raggiungibili. Import commentato per non rompere la build (noUnusedLocals).
// import { CollectionDetail } from "../pages/CollectionDetail";
import { ConfezioneDetail } from "../pages/ConfezioneDetail";
import { Contact } from "../pages/Contact";
import { Login } from "../pages/Login";
import { Signup } from "../pages/Signup";
import { VerifyEmail } from "../pages/VerifyEmail";

import { Account } from "../pages/Account";
import { Checkout } from "../pages/Checkout";
import { CheckoutSuccess } from "../pages/CheckoutSuccess";
import { CheckoutCancel } from "../pages/CheckoutCancel";
import { About } from "../pages/About";
import { Fiscalita } from "../pages/Fiscalita";
import { Impressum } from "../pages/Impressum";
import { Privacy } from "../pages/Privacy";
import { Blog } from "../pages/Blog";
import { BlogArticle } from "../pages/BlogArticle";
import { RequireAuth } from "../auth/RequireAuth";
import { detectLocale, isValidLocale } from "./locale";
import { slugFor, pathFor, legacyRedirects } from "./paths";

/**
 * Reindirizza un vecchio URL allo slug tradotto della stessa pagina, conservando
 * l'eventuale sottopagina (`/de/confezioni/roma` → `/de/kollektionen/roma`).
 * `replace` evita di lasciare il vecchio indirizzo nella cronologia del browser.
 */
function LegacyPath({ page, locale }: { page: Parameters<typeof pathFor>[0]; locale: string }) {
  const { slug } = useParams();
  // La lista collezioni non ha più un dettaglio: un eventuale slug va scartato,
  // altrimenti in EN/DE finirebbe sulla scheda prodotto (stesso slug padre).
  const sub = page === "collections" ? undefined : slug;
  return <Navigate to={pathFor(page, locale, sub)} replace />;
}

function LocaleRedirect() {
  return <Navigate to={`/${detectLocale()}`} replace />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/:locale/*" element={<LocaleGuard />} />
      <Route path="*" element={<LocaleRedirect />} />
    </Routes>
  );
}

function LocaleGuard() {
  // La lingua va letta dal router, NON da window.location: quest'ultima non è
  // reattiva, quindi durante una navigazione le rotte venivano costruite con la
  // lingua precedente. Passando da /it/collezioni a /en/collections, l'indirizzo
  // inglese finiva valutato con le rotte italiane — dove "collections" è uno
  // slug storico — e veniva rimandato indietro a /it/collezioni: il cambio
  // lingua sembrava non funzionare.
  const location = useLocation();
  const locale = location.pathname.split("/")[1];

  if (!isValidLocale(locale)) {
    return <LocaleRedirect />;
  }

  return (
    <LocaleLayout>
      <Routes>
        <Route index element={<Home />} />

        {/* ── Rotte canoniche, con slug tradotto per lingua (vedi ./paths.ts) ──
            In EN/DE la lista e il dettaglio condividono lo stesso padre
            ("collections" / "kollektionen"); in IT restano distinti. */}
        <Route path={slugFor("collections", locale)} element={<Collections />} />
        <Route path={`${slugFor("product", locale)}/:slug`} element={<ConfezioneDetail />} />
        <Route path={slugFor("about", locale)} element={<About />} />
        <Route path={slugFor("contact", locale)} element={<Contact />} />
        <Route path={slugFor("fiscal", locale)} element={<Fiscalita />} />
        <Route path={slugFor("privacy", locale)} element={<Privacy />} />
        <Route path={slugFor("impressum", locale)} element={<Impressum />} />
        <Route path={slugFor("blog", locale)} element={<Blog />} />
        <Route path={`${slugFor("blog", locale)}/:slug`} element={<BlogArticle />} />

        {/* ── Vecchi URL: restano raggiungibili e reindirizzano al canonico ──
            Senza questi, ogni link già condiviso o indicizzato finirebbe nel
            catch-all e verrebbe buttato in home, perdendo la pagina richiesta. */}
        {legacyRedirects(locale).flatMap(({ from, page }) => [
          <Route key={from} path={from} element={<LegacyPath page={page} locale={locale} />} />,
          <Route key={`${from}/:slug`} path={`${from}/:slug`} element={<LegacyPath page={page} locale={locale} />} />,
        ])}
        {/* Il dettaglio collezione è disattivato: /collezioni/:slug porta alla lista. */}
        <Route path="checkout" element={<RequireAuth><Checkout /></RequireAuth>} />
        <Route path="checkout/success" element={<CheckoutSuccess />} />
        <Route path="checkout/cancel" element={<CheckoutCancel />} />
        <Route path="account" element={<Account />} />
        <Route path="login" element={<Login />} />
        <Route path="signup" element={<Signup />} />
        <Route path="auth/verify" element={<VerifyEmail />} />
        <Route path="*" element={<Navigate to="" replace />} />
      </Routes>
    </LocaleLayout>
  );
}
